import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";

export default function TermsDialog({ open, onOpenChange, onAccept, loading }) {
    const [checked, setChecked] = useState(false);
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl rounded-md border-slate-200" data-testid="terms-dialog" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">
                        Flexible Office Pod Terms & Licence Agreement
                    </DialogTitle>
                    <Link
                        to="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="terms-dialog-full-link"
                        className="inline-flex items-center gap-1.5 text-xs text-brand hover:underline underline-offset-4 font-medium w-fit"
                    >
                        Read the full agreement <ExternalLink className="h-3 w-3" strokeWidth={2} />
                    </Link>
                </DialogHeader>
                <ScrollArea className="h-72 rounded-sm border border-slate-200 p-5 bg-slate-50">
                    <div className="text-sm text-slate-700 leading-relaxed space-y-3">
                        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono">Summary of Key Terms</p>
                        <p><strong>1. Bookable Hours.</strong> Pod use is permitted Monday through Friday only, in fixed sessions: Morning (8:00 AM – 12:00 PM) and Afternoon (1:00 PM – 5:00 PM). Full Day = both sessions.</p>
                        <p><strong>2. Credits.</strong> 1 credit = half day. 2 credits = full day. Credits expire 45 days from the date of purchase. Maximum 20 credits may be purchased per rolling 30-day window. Maximum 12 credits may be used in any rolling 14-day window.</p>
                        <p><strong>3. Cancellation.</strong> You may cancel up to 24 hours before the start of your booking and your credits will be restored. Late cancellations and no-shows are tracked: the first occurrence is waived; subsequent occurrences forfeit the credits used.</p>
                        <p><strong>4. Time Overrun.</strong> A 15–20 minute grace period applies after your session ends. Exceeding this grace period results in an automatic 1-credit deduction.</p>
                        <p><strong>5. Usage.</strong> Overnight bookings are not permitted. Personal items may not be stored between sessions. The pod must be vacated by the end of your session.</p>
                        <p><strong>6. Refunds.</strong> No automatic refunds. Refunds are issued at the sole discretion of Allied Consulting on a case-by-case basis.</p>
                        <p><strong>7. Privacy.</strong> Your booking details are only visible to you and authorised administrators. Public calendar views display "Available" or "Booked" status only — never user identities.</p>
                        <p><strong>8. Conduct.</strong> Users must conduct themselves professionally and leave the pod clean. Allied Consulting reserves the right to revoke access for repeated breaches.</p>
                        <p><strong>9. Liability.</strong> Allied Consulting is not liable for personal property left in the pod or for any indirect damages arising from use of the facility.</p>
                        <p>By proceeding, you acknowledge you have read, understood, and agree to be bound by the full Flexible Office Pod Terms and Licence Agreement.</p>
                    </div>
                </ScrollArea>
                <div className="flex items-start gap-3 pt-2">
                    <Checkbox
                        id="agree"
                        data-testid="terms-checkbox"
                        checked={checked}
                        onCheckedChange={(v) => setChecked(!!v)}
                        className="mt-1"
                    />
                    <label htmlFor="agree" className="text-sm text-slate-700 leading-relaxed cursor-pointer select-none">
                        I agree to the{" "}
                        <Link
                            to="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand font-medium hover:underline underline-offset-4"
                        >
                            Flexible Office Pod Terms and Licence Agreement
                        </Link>.
                    </label>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="terms-cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        disabled={!checked || loading}
                        data-testid="terms-confirm-btn"
                        className="bg-brand hover:bg-brand-hover text-white rounded-md"
                        onClick={() => onAccept()}
                    >
                        {loading ? "Confirming…" : "Confirm Booking"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
